import { useCallback, useEffect, useRef, useState } from "react";
import { Unauthorized, api, clearKey, setKey } from "./api";
import { type Coords, type GeoState, type ListMode } from "./types";
import Home from "./screens/Home";
import List from "./screens/List";
import Detail from "./screens/Detail";
import LogSheet from "./components/LogSheet";

type Route =
  | { s: "home" }
  | { s: "list"; mode: ListMode }
  | { s: "detail"; id: number };

const GEO_CACHE = "cigar.lastpos";

function readCachedPos(): { lat: number; lng: number; at: number } | null {
  try {
    const raw = localStorage.getItem(GEO_CACHE);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (typeof v?.lat === "number" && typeof v?.lng === "number") {
      return { lat: v.lat, lng: v.lng, at: Number(v.at) || 0 };
    }
  } catch {
    /* 壞資料就當冇 */
  }
  return null;
}

function writeCachedPos(lat: number, lng: number) {
  try {
    localStorage.setItem(GEO_CACHE, JSON.stringify({ lat, lng, at: Date.now() }));
  } catch {
    /* 私密瀏覽模式可能唔准寫，唔緊要 */
  }
}

export default function App() {
  const [route, setRoute] = useState<Route>({ s: "home" });
  const [coords, setCoords] = useState<Coords>(null);
  const [geoState, setGeoState] = useState<GeoState>("asking");
  const [geoDetail, setGeoDetail] = useState<string>("");
  const [needKey, setNeedKey] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [logging, setLogging] = useState(false);
  const [logName, setLogName] = useState("");
  const [bump, setBump] = useState(0);

  // 權限狀態純粹用嚟顯示，唔可以擋喺 getCurrentPosition 前面。
  const permRef = useRef("未查");
  useEffect(() => {
    navigator.permissions
      ?.query({ name: "geolocation" as PermissionName })
      .then((st) => { permRef.current = st.state; })
      .catch(() => { permRef.current = "唔支援"; });
  }, []);

  const askGeo = useCallback((gesture = false) => {
    // ⚠️ 呢個 function 一定要「同步」開始呼叫 getCurrentPosition。
    //
    // Safari 要求定位請求由用戶手勢觸發，而手勢授權跨過 await 就會失效。
    // 之前喺前面 await 咗 navigator.permissions.query，結果連撳「再試定位」
    // 都即刻 PERMISSION_DENIED —— 錯誤訊息正好就係「User denied Geolocation」，
    // 睇落似用戶拒絕，其實係個 app 自己搞冇咗個手勢。
    //
    // 所以：查權限狀態要另外做（上面 useEffect），唔可以喺呢條路徑上。
    const why: string[] = [
      `https=${window.isSecureContext ? "有" : "冇"}`,
      `api=${navigator.geolocation ? "有" : "冇"}`,
      `權限=${permRef.current}`,
    ];
    if (!window.isSecureContext) {
      setGeoDetail(why.join(" · "));
      setGeoState("insecure");
      return;
    }
    if (!navigator.geolocation) {
      setGeoDetail(why.join(" · "));
      setGeoState("unavailable");
      return;
    }

    setGeoState("asking");
    const detail = (extra: string) => setGeoDetail([...why, extra].join(" · "));
    const named: Record<number, string> = {
      1: "PERMISSION_DENIED（拒絕）",
      2: "POSITION_UNAVAILABLE（攞唔到）",
      3: "TIMEOUT（逾時）",
    };

    const onOk = (p: GeolocationPosition) => {
      const { latitude: lat, longitude: lng, accuracy } = p.coords;
      setCoords({ lat, lng });
      setGeoState("ok");
      detail(`準確度 ±${Math.round(accuracy)} 米`);
      writeCachedPos(lat, lng);
    };

    // 同步開始 —— 呢一句之前唔可以有 await。
    navigator.geolocation.getCurrentPosition(
      onOk,
      (e) => {
        detail(`${gesture ? "手勢" : "自動"}粗略: ${named[e.code] ?? "code=" + e.code} ${e.message}`);
        if (e.code === 1) {
          // 冇手勢嘅自動請求被拒 ≠ 用戶真係拒絕。Safari 就係噉，要撳一下
          // 先分得出。分錯咗會叫用戶去改一個冇問題嘅設定。
          setGeoState(gesture ? "denied" : "needGesture");
          return;
        }
        // 粗略失敗（多數逾時）再試精確。呢一步已經離開手勢，但 Safari 對
        // 同一頁面嘅後續請求一般照做，而 timeout 唔係權限問題。
        navigator.geolocation.getCurrentPosition(
          onOk,
          (e2) => {
            detail(`精確: ${named[e2.code] ?? "code=" + e2.code} ${e2.message}`);
            setGeoState(e2.code === 1 ? "denied" : e2.code === 3 ? "timeout" : "unavailable");
          },
          { enableHighAccuracy: true, timeout: 25000, maximumAge: 60000 },
        );
      },
      // 先試「唔要 GPS」嘅粗略定位。搵附近餐廳幾百米誤差綽綽有餘，而室內、
      // 地鐵、舊樓好多時 high accuracy 根本攞唔到 fix。
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 300000 },
    );
  }, []);

  useEffect(() => {
    // 先用上次嘅位置，等一開畫面就係按距離排，唔使等定位。
    const cached = readCachedPos();
    if (cached) setCoords({ lat: cached.lat, lng: cached.lng });
    askGeo();                       // 同時攞返一個新嘅
  }, [askGeo]);

  // 401 由任何一個 request 拋出 → 要密碼
  useEffect(() => {
    const onErr = (e: PromiseRejectionEvent) => {
      if (e.reason instanceof Unauthorized) setNeedKey(true);
    };
    window.addEventListener("unhandledrejection", onErr);
    return () => window.removeEventListener("unhandledrejection", onErr);
  }, []);

  // 開頭就試一次。唔可以淨係睇「有冇存密碼」—— 後端可能根本冇設密碼
  // （開發模式），咁就唔應該問。只有真係 401 才彈密碼畫面。
  useEffect(() => {
    api.cuisines().catch((e) => {
      if (e instanceof Unauthorized) setNeedKey(true);
    });
  }, []);

  const openDetail = (id: number) => setRoute({ s: "detail", id });

  if (needKey) {
    return (
      <div className="app">
        <div className="sign">
          <div className="sign__where">私人 logbook</div>
          <div className="sign__title">雪茄友好餐廳</div>
        </div>
        <div className="app__body" style={{ paddingTop: 22 }}>
          <div className="sec">輸入共用密碼</div>
          <div className="field">
            <input
              className="input"
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="兩部電話共用同一個密碼"
              autoFocus
            />
          </div>
          <button
            className="btn"
            disabled={!keyInput}
            onClick={() => {
              setKey(keyInput);
              setNeedKey(false);
              api.cuisines().catch(() => {
                clearKey();
                setNeedKey(true);
                setKeyInput("");
                alert("密碼唔啱");
              });
            }}
          >
            入去
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app" key={bump}>
      {route.s === "home" && (
        <Home
          coords={coords}
          geoState={geoState}
          geoDetail={geoDetail}
          onAskGeo={() => askGeo(true)}
          onPickDistrict={(c) => {
            // 手動揀區 = 當作用戶喺嗰度。照樣寫入 cache，下次開機直接用，
            // 唔使每次都揀。
            setCoords({ lat: c.lat, lng: c.lng });
            setGeoState("ok");
            setGeoDetail("手動揀嘅區");
            writeCachedPos(c.lat, c.lng);
          }}
          onNearby={() => setRoute({ s: "list", mode: { kind: "nearby" } })}
          onCuisine={(key, label, district, filters, filterLabel) =>
            setRoute({ s: "list", mode: { kind: "cuisine", key, label, district, filters, filterLabel } })}
          onVisitSearch={(filters, filterLabel) =>
            setRoute({ s: "list", mode: { kind: "visit", filters, filterLabel } })}
          onOpen={openDetail}
        />
      )}

      {route.s === "list" && (
        <List
          mode={route.mode}
          coords={coords}
          cuisineLabel={route.mode.kind === "cuisine" ? route.mode.label : undefined}
          onOpen={openDetail}
          onBack={() => setRoute({ s: "home" })}
        />
      )}

      {route.s === "detail" && (
        <Detail
          id={route.id}
          coords={coords}
          onBack={() => setRoute({ s: "home" })}
          onLog={(name) => {
            setLogName(name);
            setLogging(true);
          }}
        />
      )}

      {geoState !== "ok" && geoState !== "asking" && route.s === "home" && (
        <div className="footerbar">
          <button className="btn btn--plain" onClick={() => askGeo(true)}>
            再試一次定位
          </button>
        </div>
      )}

      {logging && route.s === "detail" && (
        <LogSheet
          venueId={route.id}
          venueName={logName}
          onClose={() => setLogging(false)}
          onSaved={() => {
            setLogging(false);
            setBump((b) => b + 1); // 重新載入，即刻見到新結論
          }}
        />
      )}
    </div>
  );
}
